import axios from 'axios';
import { getAccessToken } from '../auth';
import { envConfig } from '../config';

export enum ADD_PAYMENT_METHOD_PROCESS {
	PUBLISH_PROJECT = 'publish-project',
	GALLERY_PROFILE = 'gallery-profile',
	EDITOR_PROFILE = 'editor-profile',
}

function validateParams(params: { [key: string]: string }): {
	error: string | null;
} {
	const projectId = params.projectId;
	const branchIdentifier = params?.branchIdentifier;
	const projectVersion = params?.projectVersion;
	const workspaceId = params?.workspaceId as string;
	const workspaceSlug = params?.workspaceSlug as string;
	const process: ADD_PAYMENT_METHOD_PROCESS =
		params.process as ADD_PAYMENT_METHOD_PROCESS;

	const missingParams: string[] = [];

	if (!workspaceId) {
		missingParams.push('workspaceId');
	}

	if (!workspaceSlug) {
		missingParams.push('workspaceSlig');
	}

	let badProcessParamMessage = '';

	if (!process) {
		missingParams.push('process');
	} else if (!Object.values(ADD_PAYMENT_METHOD_PROCESS).includes(process)) {
		badProcessParamMessage += `The "process" query parameter is present, but its value is "${process}", but must be one of: "${Object.values(ADD_PAYMENT_METHOD_PROCESS).join(`", "`)}".`;
	}

	let invalidParamsCombinationMessage = '';

	let hasValidatedProjectParams: boolean = false;

	const projectScopedMissingParams = [];
	const galleryScopeExtraParams = [];

	if (process) {
		if (
			process === ADD_PAYMENT_METHOD_PROCESS.EDITOR_PROFILE ||
			process === ADD_PAYMENT_METHOD_PROCESS.PUBLISH_PROJECT
		) {
			if (!projectId) {
				projectScopedMissingParams.push('projectId');
			}

			if (!branchIdentifier) {
				projectScopedMissingParams.push('branchIdentifier');
			}

			if (!projectVersion) {
				projectScopedMissingParams.push('projectVersion');
			}

			if (!!projectScopedMissingParams.length) {
				hasValidatedProjectParams = true;
			}
		} else if (process === ADD_PAYMENT_METHOD_PROCESS.GALLERY_PROFILE) {
			if (!!projectId) {
				galleryScopeExtraParams.push('projectId');
			}

			if (!!branchIdentifier) {
				galleryScopeExtraParams.push('branchIdentifier');
			}

			if (!!projectVersion) {
				galleryScopeExtraParams.push('projectVersion');
			}

			if (!!galleryScopeExtraParams.length) {
				hasValidatedProjectParams = true;
			}
		}
	}

	let badProcessCombinationWithProjectIdMessage = '';

	if (projectId && !hasValidatedProjectParams) {
		if (!branchIdentifier) {
			projectScopedMissingParams.push('branchIdentifier');
		}

		if (!projectVersion) {
			projectScopedMissingParams.push('projectVersion');
		}

		if (
			process !== ADD_PAYMENT_METHOD_PROCESS.EDITOR_PROFILE &&
			process !== ADD_PAYMENT_METHOD_PROCESS.PUBLISH_PROJECT
		) {
			badProcessCombinationWithProjectIdMessage += `The "process" query parameter must be "${ADD_PAYMENT_METHOD_PROCESS.EDITOR_PROFILE}" or "${ADD_PAYMENT_METHOD_PROCESS.PUBLISH_PROJECT}" when the "projectId" query param is present but currently is "${process}".`;
		}
	}

	if (
		!!missingParams.length ||
		!!badProcessParamMessage ||
		!!invalidParamsCombinationMessage ||
		!!badProcessCombinationWithProjectIdMessage ||
		!!projectScopedMissingParams.length ||
		!!galleryScopeExtraParams.length
	) {
		let message = '';

		if (!!missingParams.length) {
			message += ` The following query parameters must always be present "${missingParams.join(`", "`)}".`;
		}

		message += !!badProcessParamMessage ? ` ${badProcessParamMessage}` : '';
		message += !!invalidParamsCombinationMessage
			? ` ${invalidParamsCombinationMessage}`
			: '';
		message += !!badProcessCombinationWithProjectIdMessage
			? ` ${badProcessCombinationWithProjectIdMessage}`
			: '';

		if (!!projectScopedMissingParams.length) {
			message += ` The following query parameters must be present if the "projectId" param is present, or if the "process" param is "${ADD_PAYMENT_METHOD_PROCESS.EDITOR_PROFILE}" or "${ADD_PAYMENT_METHOD_PROCESS.PUBLISH_PROJECT}": "${projectScopedMissingParams.join(`", "`)}".`;
		}

		if (galleryScopeExtraParams) {
			message += ` The following query parameters are not allowed to be present when the "process" param is "${ADD_PAYMENT_METHOD_PROCESS.GALLERY_PROFILE}": "${projectScopedMissingParams.join(`", "`)}".`;
		}

		return {
			error: `Cant initialize add payment method:${message}`,
		};
	}

	return {
		error: null,
	};
}

export async function startAddPaymentMethodSession(params: {
	projectId?: string;
	process: ADD_PAYMENT_METHOD_PROCESS;
	workspaceId: string;
	workspaceSlug: string;
	branchIdentifier?: string;
	projectVersion?: string;
}) {
	const res = validateParams(params);

	if (!!res.error) {
		throw new Error(res.error)
	}

	// Build query string
	const queryString =
		Object.keys(params).length === 0
			? ''
			: '?' +
				Object.keys(params)
					.filter((key) => !!(params as { [key: string]: string })[key])
					.map(
						(key: string) =>
							key + '=' + (params as { [key: string]: string })[key],
					)
					.join('&');

	const response = await axios.get(
		`${envConfig.API_BASE_URL}/pricing-service/v1/init-add-payment-method-session${queryString}`,
		{
			headers: {
				Authorization: 'Bearer ' + (await getAccessToken()),
			},
		},
	);

	const redirectURL = response.data?.data?.url;

	// Redirect to session url
	window.location.href = redirectURL;

	return response.data?.data;
}
